import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Subject, Observable, Subscription, BehaviorSubject } from "rxjs";
import * as _ from "underscore";
import { RootStoreState } from "src/app/root-store";
import { takeUntil, debounceTime, distinctUntilChanged } from "rxjs/operators";

import { ActivatedRoute, Router } from "@angular/router";
import {
  createRentalQuoteError,
  createRentalQuoteLoading,
  createRentalQuoteSuccess,
  getCreatedRentalQuote,
} from "src/app/root-store/kwikspace-app-store/selectors/quote.selector";
import {
  ClearProductConfiguration,
  ClearProductList,
  CreateProductConfiguration,
  GetProductList,
} from "src/app/root-store/kwikspace-app-store/actions/product.action";
import {
  ClearCustomerList,
  GetCustomerList,
} from "src/app/root-store/kwikspace-app-store/actions/customer.action";
import {
  getCustomerList,
  getCustomerListError,
  getCustomerListLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/customer.selector";
import {
  getCreatedProductConfiguration,
  getCreatedProductConfigurationError,
  getCreatedProductConfigurationLoading,
  getProductList,
  getProductListError,
  getProductListLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/product.selector";
import { getCurrentUser } from "src/app/root-store/main-app-store/selectors/auth.selector";
import { Product } from "src/app/models/Product.model";
import { Customer } from "src/app/models/Customer.model";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CreateRentalQuote } from "src/app/root-store/kwikspace-app-store/actions/quote.action";
import { AppRoutes } from "src/app/shared/endpoints.enum";
import { TanToastrService } from "src/app/services/toastr.service";
import {
  getCreatedLineItems,
  getCreatedLineItemsError,
  getCreatedLineItemsLoading,
  getRentalCategoryError,
  getRentalCategoryList,
  getRentalCategoryLoading,
  getRentalCategorySizeError,
  getRentalCategorySizeList,
  getRentalCategorySizeLoading,
  getRentalPeriods,
  getRentalPeriodsError,
  getRentalPeriodsLoading,
  getRentalPriceList,
  getRentalPriceListError,
  getRentalPriceListLoading,
  getRentalSizes,
  getRentalSizesError,
  getRentalSizesLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/rental.selector";
import {
  ClearRentalLineItems,
  ClearRentalList,
  CreateRentalLineItem,
  GetRentalList,
  GetRentalCategory,
  GetRentalPeriod,
  GetRentalPrice,
  GetRentalSize,
  GetRentalCategorySize,
  ClearRentalSize,
} from "src/app/root-store/kwikspace-app-store/actions/rental.action";
import { Rental } from "src/app/models/Rental.model";
import {
  ClearTransportAreas,
  ClearTransportDistricts,
  ClearTransportPriceList,
  GetTransportAreas,
  GetTransportDistricts,
  GetTransportLoads,
  GetTransportOptions,
  GetTransportPriceList,
} from "src/app/root-store/kwikspace-app-store/actions/transport.action";
import { Transport } from "src/app/models/Transport.model";
import {
  getTransportAreas,
  getTransportAreasError,
  getTransportAreasLoading,
  getTransportDistricts,
  getTransportDistrictsError,
  getTransportDistrictsLoading,
  getTransportLoads,
  getTransportLoadsError,
  getTransportLoadsLoading,
  getTransportOptions,
  getTransportOptionsError,
  getTransportOptionsLoading,
  getTransportPriceList,
  getTransportPriceListError,
  getTransportPriceListLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/transport.selector";
import { RentalSize } from "src/app/models/RentalSize.model";
import { RentalPeriod } from "src/app/models/RentalPeriod.model";
import { ModalComponent } from "src/app/core/components/modal/modal.component";
import {
  GetBuildings,
  GetLocations,
  GetOrganizations,
  GetProvinces,
  GetValidities,
} from "src/app/root-store/kwikspace-app-store/actions/kwikspace.action";
import {
  getBuildings,
  getBuildingsError,
  getBuildingsLoading,
  getLocations,
  getLocationsError,
  getLocationsLoading,
  getOrganizations,
  getOrganizationsError,
  getOrganizationsLoading,
  getProvinces,
  getProvincesError,
  getProvincesLoading,
  getValidities,
  getValiditiesError,
  getValiditiesLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/kwikspace.selector";
import { timeStamp } from "console";

@Component({
  selector: "kwikspace-new-rental-quote",
  templateUrl: "./new-rental-quote.component.html",
  styleUrls: ["./new-rental-quote.component.scss"],
})
export class NewRentalQuoteComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private cancelationSubscription = new Subscription();

  @ViewChild("optionalExtraModal", { static: false })
  optionalExtraModal: ModalComponent;

  @ViewChild("editOptionalExtraModal", { static: false })
  editOptionalExtraModal: ModalComponent;

  @ViewChild("drawingModal", { static: false })
  drawingModal: ModalComponent;

  @ViewChild("unitAddedModal", { static: false })
  unitAddedModal: ModalComponent;

  @ViewChild("removeExtrasModal", { static: false })
  removeExtrasModal: ModalComponent;

  newRentalQuoteLoading$: Observable<any> = this.store.select(
    createRentalQuoteLoading
  );
  newRentalQuoteError$: Observable<any> = this.store.select(
    createRentalQuoteError
  );

  customersLoading$: Observable<any> = this.store.select(
    getCustomerListLoading
  );
  customersError$: Observable<any> = this.store.select(getCustomerListError);

  productsLoading$: Observable<any> = this.store.select(getProductListLoading);
  productsError$: Observable<any> = this.store.select(getProductListError);

  rentalPricingLoading$: Observable<any> = this.store.select(
    getRentalPriceListLoading
  );
  rentalPricingError$: Observable<any> = this.store.select(
    getRentalPriceListError
  );

  rentalSizesLoading$: Observable<any> = this.store.select(
    getRentalSizesLoading
  );
  rentalSizesError$: Observable<any> = this.store.select(getRentalSizesError);

  rentalPeriodLoading$: Observable<any> = this.store.select(
    getRentalPeriodsLoading
  );
  rentalPeriodError$: Observable<any> = this.store.select(
    getRentalPeriodsError
  );

  rentalCategoryLoading$: Observable<any> = this.store.select(
    getRentalCategoryLoading
  );
  rentalCategoryError$: Observable<any> = this.store.select(
    getRentalCategoryError
  );

  rentalCategorySizeLoading$: Observable<any> = this.store.select(
    getRentalCategorySizeLoading
  );
  rentalCategorySizeError$: Observable<any> = this.store.select(
    getRentalCategorySizeError
  );

  transportPricingLoading$: Observable<any> = this.store.select(
    getTransportPriceListLoading
  );
  transportPricingError$: Observable<any> = this.store.select(
    getTransportPriceListError
  );

  transportOptionsLoading$: Observable<any> = this.store.select(
    getTransportOptionsLoading
  );
  transportOptionsError$: Observable<any> = this.store.select(
    getTransportOptionsError
  );

  transportLoadsLoading$: Observable<any> = this.store.select(
    getTransportLoadsLoading
  );
  transportLoadsError$: Observable<any> = this.store.select(
    getTransportLoadsError
  );

  transportAreasLoading$: Observable<any> = this.store.select(
    getTransportAreasLoading
  );
  transportAreasError$: Observable<any> = this.store.select(
    getTransportAreasError
  );

  transportDistrictsLoading$: Observable<any> = this.store.select(
    getTransportDistrictsLoading
  );
  transportDistrictsError$: Observable<any> = this.store.select(
    getTransportDistrictsError
  );

  createProductConfigurationLoading$: Observable<any> = this.store.select(
    getCreatedProductConfigurationLoading
  );
  createProductConfigurationError$: Observable<any> = this.store.select(
    getCreatedProductConfigurationError
  );

  createLineItemLoading$: Observable<any> = this.store.select(
    getCreatedLineItemsLoading
  );
  createLineItemError$: Observable<any> = this.store.select(
    getCreatedLineItemsError
  );

  provincesLoading$: Observable<any> = this.store.select(getProvincesLoading);
  provincesError$: Observable<any> = this.store.select(getProvincesError);

  buildingsLoading$: Observable<any> = this.store.select(getBuildingsLoading);
  buildingsError$: Observable<any> = this.store.select(getBuildingsError);

  organizationsLoading$: Observable<any> = this.store.select(
    getOrganizationsLoading
  );
  organizationsError$: Observable<any> = this.store.select(
    getOrganizationsError
  );

  locationsLoading$: Observable<any> = this.store.select(getLocationsLoading);
  locationsError$: Observable<any> = this.store.select(getLocationsError);

  validitiesLoading$: Observable<any> = this.store.select(getValiditiesLoading);
  validitiesError$: Observable<any> = this.store.select(getValiditiesError);

  rentalLoading: boolean = false;
  transportOneLoading: boolean = false;
  transportOneHasPricing: boolean = false;
  transportTwoLoading: boolean = false;
  transportTwoHasPricing: boolean = false;

  customers: Customer[] = [];
  products: Product[] = [];
  filteredProducts: Product[] = [];
  rentalPricing: Rental[] = [];
  rentalOptions: Rental[] = [];
  rentalSizes: RentalSize[] = [];
  rentalPeriods: RentalPeriod[] = [];
  rentalCategories: any[] = [];
  transportPricing: Transport[] = [];
  transportOptions: any[] = [];
  transportLoads: any[] = [];
  transportAreas: any[] = [];
  transportDistricts: any[] = [];

  provinces: any[] = [];
  buildingTypes: any[] = [];
  organizationalTypes: any[] = [];
  quoteLocations: any[] = [];
  quoteValidities: any[] = [];

  selectedProvince;
  selectedDistrict;
  selectedArea;
  selectedBuildingType;
  selectedOrganizationalSector;

  selectedSize: string = null;
  selectedPeriod: string = null;
  selectedRentalCategory: string = null;
  selectedOption: any = null;
  selectedOptionId: any = null;
  selectedProduct: any = null;
  selectedProductId: any = null;
  selectedTransport: any = null;
  showOptionDescription: boolean = false;
  showTransport: boolean = false;
  showRentalOptions: boolean = false;
  showOptionalExtras: boolean = false;
  hasPricing: boolean = false;
  hasTransportArea: boolean = false;
  hasTransportPricing: boolean = false;
  retryAreas: boolean = false;
  showTransportSize: boolean = false;
  showTransportOption: boolean = false;
  isSaProvince: boolean = false;

  showProvinceMsg: boolean = false;
  showDistricts: boolean = false;
  showAreas: boolean = false;
  isClassroom: boolean = false;
  showTransportGuide: boolean = false;

  createdQuote: any = null;

  addressForm: FormGroup;
  quoteForm: FormGroup;
  rentalForm: FormGroup;
  extrasForm: FormGroup;
  editExtrasForm: FormGroup;

  user: any = null;
  isAdmin: boolean = false;
  isStaff: boolean = false;
  isCustomer: boolean = false;
  customerSelected: boolean = true;
  locationSelected: boolean = true;

  customerId: any = null;
  requiredBy: any = null;
  quoteNumber: any = null;

  searchText: string = "";
  searchTextProduct: string = "";

  quoteLineItem: any = {};
  quoteLineItemProducts: any[] = [];
  quoteCart: any[] = [];

  extraToEdit = null;
  extraToEditName = null;

  extrasFormIsDisabled: boolean = true;
  editExtrasFormIsDisabled: boolean = true;

  canSave: boolean = false;

  addedProducts: any[] = [];
  canCreateLineItems: boolean = false;
  productsAdded: any = 0;

  lineItemsToAdd = 0;
  lineItemsAdded = 0;
  addedLineItems: any[] = [];

  addOnsToAdd = 0;
  addOnsAdded = 0;

  canAddProducts: boolean = false;

  selectedTransportLoad = null;
  selectedTransportLoadOne = null;
  selectedTransportLoadTwo = null;
  selectedTransportArea = null;
  selectedTransportOption = null;
  selectedTransportJoinOption = null;
  selectedLocation = null;
  addSecondTransportLoad: boolean = false;
  loadOneQtyUpdate = new Subject<number>();
  loadTwoQtyUpdate = new Subject<number>();
  editingTransportLoad = null;
  transportType = "load1";

  showTransportCharge: boolean = false;
  showLocationDD: boolean = false;

  actualMonths: number = 1;
  unitQty: number = 1;
  rentalMonthsErrorMsg: string = "Actual months is required";
  rentalMonthsError: boolean = true;
  qtyEntered: boolean = false;
  showQty: boolean = true;

  limitationError: boolean = false;
  limitation: any = "";

  canSaveQuote: boolean = false;
  periodIsSet: boolean = false;
  showPeriodDD: boolean = true;
  showSize: boolean = false;

  drawingImg: any = null;

  activeStep: number = 1;
  stepper = [
    {
      id: 1,
      name: "Quote Details",
      status: "progress",
    },
    {
      id: 2,
      name: "Quote Items",
      status: "todo",
    },
  ];

  isMobile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    window.innerWidth <= 768
  );

  @HostListener("window:resize", ["$event"])
  setIsMobile(event) {
    const isMobile = event.target.innerWidth <= 768;
    this.isMobile.next(isMobile);
  }

  constructor(
    private store: Store<RootStoreState.RootState>,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private toastrService: TanToastrService
  ) { }

  ngOnInit() {
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          // debugger
          this.user = res;
          this.isStaff = res.isStaff;
          this.isCustomer = !res.isStaff && res.customer != null ? true : false;
          this.isAdmin = res.isSuperuser;

          if (this.isStaff && !this.isCustomer) {
            this.customerSelected = false;
            if (this.user.salesRep && this.user.salesRep.locations.length > 1) {
              this.locationSelected = false;
              this.showLocationDD = true;
              this.quoteLocations = this.user.salesRep.locations;
            }
            this.getCustomers();
          } else {
            this.locationSelected = false;
            this.getSalesLocations();
          }
        }
      });

    this.store
      .select(getProvinces)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((provinces: any) => {
        if (provinces && provinces.results) {
          this.provinces = provinces.results;
        }
      });

    this.store
      .select(getBuildings)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((buildings: any) => {
        if (buildings) {
          this.buildingTypes = buildings;
        }
      });

    this.store
      .select(getOrganizations)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((organizations: any) => {
        if (organizations) {
          this.organizationalTypes = organizations;
        }
      });

    this.store
      .select(getValidities)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((validities: any) => {
        if (validities) {
          this.quoteValidities = validities;
        }
      });

    this.store
      .select(getTransportLoads)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((loads: any) => {
        if (loads) {
          if (loads.length > 0) {
            this.transportLoads = loads.filter(
              (loadSize) => loadSize.available == true
            );
            this.transportLoads = this.transportLoads.filter(
              (loadSize) => loadSize.hasRental == true
            );
          }
        }
      });

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (this.isClassroom) return;

        if (transports?.results?.length > 0) {
          if (
            this.transportType === "load1" &&
            this.selectedTransportLoadOne?.results === null
          ) {
            this.filterTransportList("load1", transports.results, this.editingTransportLoad);
          } else if (
            this.transportType === "load2" &&
            this.selectedTransportLoadTwo?.results === null
          ) {
            this.filterTransportList("load2", transports.results, this.editingTransportLoad);
          }
        } else if (transports?.results?.length === 0) {
          if (this.transportType === "load1") {
            this.transportOneHasPricing = false;
            this.transportOneLoading = false;
          } else if (this.transportType === "load2") {
            this.transportTwoHasPricing = false;
            this.transportTwoLoading = false;
          }
        }
      });

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (!this.isClassroom) return;

        if (transports && transports.results) {
          if (transports.results.length > 0) {
            this.transportPricing = [];

            transports.results.forEach((price) => {
              if (
                price.priceType.name == "Both" ||
                price.priceType.name == "Rentals"
              ) {
                if (
                  price.transportCharge.name == "Transport" ||
                  price.transportCharge.name == "Delivery and Collection" ||
                  price.transportCharge.name ==
                  "Travel & Siting (Joining) on site" ||
                  price.transportCharge.name == "Siting and Dismantling"
                ) {
                  this.transportPricing.push(price);
                }
              }
            });

            this.hasTransportPricing =
              this.transportPricing.length > 0 ? true : false;
            this.setTransportPricing();
          }
        }
      });

    this.quoteForm = this.formBuilder.group({
      customer: [{ value: null, disabled: true }, [Validators.required]],
      searchCustomer: [null, []],
      building_type: [null, [Validators.required]],
      organization_sector: [null, [Validators.required]],
      your_reference: [null, []],
      customer_account_number: [null, []],
      site_allocation: [null, []],
      quote_validity: [null, []],
      quote_location: [null, []],
      notes: [null, []],
      line_items: [null, []],
      purchase_line_items: [null, []],
    });

    this.addressForm = this.formBuilder.group({
      shipping_address1: [null, [Validators.required]],
      shipping_address2: [null, []],
      shipping_suburb: [null, [Validators.required]],
      shipping_province: [null, [Validators.required]],
      shipping_district: [null, []],
      shipping_area: [null, []],
      shipping_postal: [null, []],
    });

    this.rentalForm = this.formBuilder.group({
      rental_period: [null, [Validators.required]],
      rental_months: [null, [Validators.required]],
      rental_size: [null, [Validators.required]],
      rental_option: [null, [Validators.required]],
      rental_option_category: [null, [Validators.required]],
      unit_qty: [null, [Validators.required]],
      optional_extras: [null, []],
      rental_transport: [null, [Validators.required]],
      transport_option: [null, [Validators.required]],
      transport_area: [null, [Validators.required]],
      transport_load_one: [null, [Validators.required]],
      transport_load_one_qty: [null, [Validators.required]],
      transport_load_two: [null, []],
      transport_load_two_qty: [null, []],
      transport_config: [null, [Validators.required]],
    });

    this.subscribeToSubjects();

    this.extrasForm = this.formBuilder.group({
      product: [null, [Validators.required]],
      searchProduct: [null, []],
      quantity: [null, [Validators.required]],
      name: [null, []],
    });

    this.editExtrasForm = this.formBuilder.group({
      product: [null, [Validators.required]],
      searchProduct: [null, []],
      quantity: [null, [Validators.required]],
      name: [null, []],
    });

    this.store.dispatch(new GetProvinces());
    this.store.dispatch(new GetBuildings());
    this.store.dispatch(new GetOrganizations());
    this.store.dispatch(new GetValidities());
    this.store.dispatch(new GetTransportLoads());
    this.store.dispatch(new ClearRentalLineItems());
    this.store.dispatch(new ClearProductConfiguration());
  }

  getSalesLocations() {
    this.store
      .select(getLocations)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((locations: any) => {
        if (locations && locations.results) {
          this.showLocationDD = true;
          this.quoteLocations = locations.results;
        }
      });

    this.store.dispatch(new GetLocations());
  }

  setSelectedQuoteLocation() {
    this.selectedLocation = this.quoteForm.value.quote_location;
    this.locationSelected = this.selectedLocation != null ? true : false;
  }

  setProvince() {
    this.store.dispatch(new ClearTransportDistricts());
    this.store.dispatch(new ClearTransportAreas());

    this.selectedProvince = this.addressForm.value.shipping_province;

    const _id = this.provinces.findIndex(
      (obj) => obj.id == this.selectedProvince
    );
    const province = this.provinces[_id].name;

    if (province != "Other") {
      this.canSaveQuote = false;
      this.showProvinceMsg = false;
      this.isSaProvince = true;
      this.showDistricts = true;
      this.getTransportDistricts();
    } else {
      this.canSaveQuote = true;
      this.showProvinceMsg = true;
      this.showDistricts = false;
      this.showAreas = false;
    }
  }

  setBuildingType() {
    this.selectedBuildingType = this.quoteForm.value.building_type;
  }

  setOrganizationalSector() {
    this.selectedOrganizationalSector =
      this.quoteForm.value.organization_sector;
  }

  setTransportPricing() {
    if (this.transportPricing.length > 0) {
      this.setTransportOption();
    } else {
      this.showTransportCharge = true;
    }
  }

  setTransportOption() {
    let _id;
    let _option;
    let _transportOption = null;
    let _transportOptionJoin = null;

    for (let i = 0; i < this.transportPricing.length; i++) {
      _option = this.transportPricing[i];

      if (_option.transportCharge.name == "Transport") {
        _transportOption = _option;
        this.selectedTransport = _option;
      } else if (
        _transportOption == null &&
        _option.transportCharge.name == "Delivery and Collection"
      ) {
        _transportOption = _option;
        this.selectedTransport = _option;
      }

      if (_option.transportCharge.name == "Travel & Siting (Joining) on site") {
        _transportOptionJoin = _option;
        this.selectedTransportJoinOption = _option;
      } else if (
        _transportOptionJoin == null &&
        _option.transportCharge.name == "Siting and Dismantling"
      ) {
        _transportOptionJoin = _option;
        this.selectedTransportJoinOption = _option;
      }
    }

    if (this.selectedTransport == null) {
      const transportId = this.rentalForm.value.rental_transport;
      _id = this.transportPricing.findIndex((obj) => obj.id == transportId);
      this.selectedTransport = this.transportPricing[_id];
    }

    const transport = {
      id: this.selectedTransport.id,
      transport: this.selectedTransport,
    };

    this.quoteLineItem.transportOne = transport;

    const requireJoin = this.requireJoining();
    if (requireJoin) {
      if (this.selectedTransportJoinOption != null) {
        const transportJoin = {
          id: this.selectedTransportJoinOption.id,
          transport: this.selectedTransportJoinOption,
        };

        this.quoteLineItem.transportJoin = transportJoin;
      }
    }

    this.showOptionDescription = this.selectedOption != null ? true : false;

    this.rentalLoading = false;
  }

  setRentalCategory() {
    if (this.isClassroom && this.selectedRentalCategory == null) {
      const _id = this.rentalCategories.findIndex(
        (obj) => obj.name == "Classroom"
      );
      this.selectedRentalCategory = this.rentalCategories[_id].id;
    } else {
      this.selectedRentalCategory =
        this.rentalForm.value.rental_option_category;

      this.getRentalCategorySize();
      this.toggleSizeDD();
    }
  }

  setPeriod() {
    this.selectedPeriod = this.rentalForm.value.rental_period;

    if (this.isClassroom) {
      this.handleClassroom();
    } else {
      this.getRentalOptions();
      this.toggleSizeDD();
    }

    this.validateMinMaxMonths();
  }

  toggleSizeDD() {
    this.showSize =
      this.selectedRentalCategory != null && this.selectedPeriod != null
        ? true
        : false;
  }

  getRentalCategorySize() {
    this.store
      .select(getRentalCategorySizeList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res && res.length > 0) {
          this.filterSizeDropdown(res);
        }
      });

    let params = `?category=${this.selectedRentalCategory}`;
    this.store.dispatch(new GetRentalCategorySize(params));
  }

  filterSizeDropdown(categorySizes) {
    let sizes = [];
    const availableSize = [
      ...new Map(categorySizes.map((item) => [item.size.size, item])).values(),
    ];

    for (let i = 0; i < availableSize.length; i++) {
      const item = availableSize[i] as any;
      sizes.push(item.size);
    }

    this.rentalSizes = sizes;
  }

  setSize() {
    this.selectedSize = this.rentalForm.value.rental_size;
    this.getRentalOptions();
  }

  setRentalOption() {
    this.rentalLoading = true;
    this.showTransportGuide = true;

    this.selectedOptionId = this.rentalForm.value.rental_option;
    const _id = this.rentalPricing.findIndex(
      (obj) => obj.id == this.selectedOptionId
    );
    this.selectedOption = this.rentalPricing[_id];
    this.isClassroom = this.selectedOption.category.name.includes("Classroom");

    const lineItem = {
      name: "Unit " + (this.quoteCart.length + 1),
      idx: this.quoteCart.length + 1,
      item: this.selectedOption,
      rental_period: this.rentalForm.value.rental_months,
      quantity: this.rentalForm.value.unit_qty
        ? this.rentalForm.value.unit_qty
        : 1,
      transportOne: "",
      transportTwo: "",
      transportJoin: "",
      extras: [],
      selected_period_id: this.selectedPeriod,
    };

    this.quoteLineItem = lineItem;

    if (this.isSaProvince) {
      this.showTransport = true;

      if (this.isClassroom) {
        this.getTransportPricing();
      } else {
        this.showTransportSize = true;
        this.rentalLoading = false;
      }
    } else {
      this.showOptionDescription = this.selectedOption != null ? true : false;
    }
  }

  getRentalOptions() {
    if (
      this.selectedPeriod != null &&
      this.selectedSize != null &&
      this.selectedRentalCategory != null
    ) {
      this.getRentalPricing();
      this.showRentalOptions = true;
    } else {
      this.showRentalOptions = false;
    }
  }

  getCustomers() {
    this.store
      .select(getCustomerList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((customers: any) => {
        if (customers && customers.results) {
          this.customers = customers.results;
        }
      });

    this.store.dispatch(new GetCustomerList({}));
  }

  getProducts() {
    this.store.dispatch(new ClearProductList());
    this.store.dispatch(new GetProductList({}));

    this.store
      .select(getProductList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((products: any) => {
        if (products && products.length > 0) {
          let matchedProducts = [];
          let selectedRentalOption = this.selectedOption;

          this.products = products;

          if (selectedRentalOption) {
            const unitSize = selectedRentalOption.size.size;
            const unitType = selectedRentalOption.item.description;

            this.products.forEach((product: any) => {
              if (
                product.size === unitSize &&
                product.office.toLowerCase() === unitType.toLowerCase()
              ) {
                matchedProducts.push(product);
              } else if (product.size == "0" && product.office == "0") {
                matchedProducts.push(product);
              }
            });

            this.filteredProducts = matchedProducts;
          } else {
            this.filteredProducts = this.products;
          }
        }
      });
  }

  getRentalPricing() {
    this.store.dispatch(new ClearRentalList());

    this.store
      .select(getRentalPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rentals: any) => {
        if (rentals && rentals.results) {
          if (rentals.results.length > 0) {
            this.hasPricing = true;
            this.rentalPricing = rentals.results;

            if (this.isClassroom) {
              this.selectedOption = this.rentalPricing[0];
              this.setRentalOption();
            }
          } else {
            this.hasPricing = false;
          }
        }
      });

    let params = `?period=${this.selectedPeriod}&size=${this.selectedSize}&category=${this.selectedRentalCategory}`;
    this.store.dispatch(new GetRentalPrice(params));
  }

  getRentalPriceList() {
    this.store.dispatch(new ClearRentalList());

    this.store
      .select(getRentalPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rentals: any) => {
        if (rentals && rentals.results) {
          if (rentals.results.length > 0) {
            this.hasPricing = true;
            this.rentalPricing = rentals.results;
          }
        }
      });

    this.store.dispatch(new GetRentalList({}));
  }

  getRentalSizes() {
    this.store.dispatch(new ClearRentalSize());

    this.store
      .select(getRentalSizes)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sizes) => {
        if (sizes) {
          this.rentalSizes = sizes;
        }
      });

    this.store.dispatch(new GetRentalSize());
  }

  getRentalPeriods() {
    this.store
      .select(getRentalPeriods)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((periods) => {
        if (periods) {
          this.rentalPeriods = periods;
        }
      });

    this.store.dispatch(new GetRentalPeriod());
  }

  getRentalCategories() {
    this.store
      .select(getRentalCategoryList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => {
        if (categories) {
          this.rentalCategories = categories;
        }
      });

    this.store.dispatch(new GetRentalCategory());
  }

  getTransportPricing() {
    this.store.dispatch(new GetTransportLoads());

    this.store
      .select(getTransportLoads)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((loads: any) => {
        if (loads) {
          if (loads.length > 0) {
            this.transportLoads = loads.filter(
              (loadSize) => loadSize.available == true
            );
            this.transportLoads = this.transportLoads.filter(
              (loadSize) => loadSize.hasRental == true
            );

            this.store.dispatch(new GetTransportOptions());

            if (this.isClassroom) {
              const loadId = this.transportLoads.findIndex(
                (obj) => obj.size == "7m x 7m"
              );
              this.selectedTransportLoad = this.transportLoads[loadId].id;

              if (this.selectedTransportLoad) {
                this.setSelectedTransportLoad();
              }
            }
          }
        }
      });

    this.store
      .select(getTransportOptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options: any) => {
        if (options) {
          this.transportOptions = options;
          let transportId: any = null;
          let transportJoinId: any = null;

          this.transportOptions.forEach((option) => {
            if (option.name == "Transport") {
              transportId = option.id;
            }
          });

          if (transportId == null) {
            this.transportOptions.forEach((option) => {
              if (option.name == "Delivery and Collection") {
                transportId = option.id;
              }
            });
          }

          this.selectedTransportOption = transportId;

          if (this.selectedTransportOption) {
            this.setSelectedTransportOption();
          }

          if (this.isClassroom) {
            this.transportOptions.forEach((option) => {
              if (option.name == "Travel & Siting (Joining) on site") {
                transportJoinId = option.id;
              }
            });

            if (transportJoinId == null) {
              this.transportOptions.forEach((option) => {
                if (option.name == "Siting and Dismantling") {
                  transportJoinId = option.id;
                }
              });
            }

            this.selectedTransportJoinOption = transportJoinId;
            if (this.selectedTransportJoinOption) {
              this.setSelectedTransportOption();
            }
          }
        }
      });
  }

  getTransportAllPricing() {
    this.store.dispatch(new ClearTransportPriceList());
    this.store.dispatch(new GetTransportPriceList(""));

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (transports && transports.results) {
          this.showTransportSize = true;

          this.transportPricing = [];
          transports.results.forEach((price) => {
            if (
              price.priceType.name == "Both" ||
              price.priceType.name == "Rentals"
            ) {
              this.transportPricing.push(price);
            }
          });

          this.store.dispatch(new GetTransportOptions());

          this.hasTransportPricing =
            this.transportPricing.length > 0 ? true : false;
        }
      });

    this.store
      .select(getTransportOptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options: any) => {
        if (options) {
          let transportId: any = null;

          options.forEach((option) => {
            if (option.name == "Transport") {
              transportId = option.id;
            }
          });

          if (transportId == null) {
            options.forEach((option) => {
              if (option.name == "Delivery and Collection") {
                transportId = option.id;
              }
            });
          }

          this.selectedTransportOption = transportId;

          if (this.selectedTransportOption) {
            this.setSelectedTransportOption();
          }
        }
      });
  }

  requireJoining() {
    let join = false;

    const _id = this.rentalSizes.findIndex(
      (obj) => obj.id == this.selectedSize
    );
    const size =
      _id >= 0 && this.rentalSizes[_id].size ? this.rentalSizes[_id].size : 0;

    let min = size.split("m x")[0];
    min = parseInt(min);

    join = min > 3 ? true : false;

    return join;
  }

  getTransportDistricts() {
    const payload = {
      filter: {
        filterCategory: "province",
        filter: this.selectedProvince,
      },
    };

    this.store.dispatch(new GetTransportDistricts(payload));

    this.store
      .select(getTransportDistricts)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((districts: any) => {
        if (districts) {
          this.transportDistricts = districts;
        }
      });
  }

  setDistrict() {
    this.selectedDistrict = this.addressForm.value.shipping_district;
    this.showAreas = true;
    this.getTransportAreas();
  }

  getTransportAreas() {
    const payload = {
      filter: {
        filterCategory: "district",
        filter: this.selectedDistrict,
      },
    };

    this.store.dispatch(new GetTransportAreas(payload));

    this.store
      .select(getTransportAreas)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((areas: any) => {
        if (areas) {
          this.transportAreas = areas;
        }
      });
  }

  setArea() {
    this.selectedArea = this.addressForm.value.shipping_area;
    this.canSaveQuote = true;
  }

  setSelectedTransportLoad() {
    this.selectedTransportLoad = this.selectedTransportLoad
      ? this.selectedTransportLoad
      : this.rentalForm.value.transport_load;

    this.getTransportPriceOptions();
  }

  addAnotherTruck() {
    this.addSecondTransportLoad = true;
  }

  setSelectedTransportLoadOne() {
    this.selectedTransportLoadOne = {
      load: this.rentalForm.value.transport_load_one,
      quantity: this.rentalForm.value.transport_load_one_qty,
      results: null,
    };
    if (
      this.selectedTransportLoadOne.load &&
      this.selectedTransportLoadOne.quantity
    ) {
      this.transportOneLoading = true;
      this.transportType = "load1";
      this.editingTransportLoad = this.selectedTransportLoadOne;
      this.calculateTransportPricing();
    }
  }

  setSelectedTransportLoadTwo() {
    this.selectedTransportLoadTwo = {
      load: this.rentalForm.value.transport_load_two,
      quantity: this.rentalForm.value.transport_load_two_qty,
      results: null,
    };
    if (
      this.selectedTransportLoadTwo.load &&
      this.selectedTransportLoadTwo.quantity
    ) {
      this.transportTwoLoading = true;
      this.transportType = "load2";
      this.editingTransportLoad = this.selectedTransportLoadTwo;
      this.calculateTransportPricing();
    }
  }

  calculateTransportPricing() {
    const params = `?load_size=${this.editingTransportLoad.load}&transport_location=${this.createdQuote.shipping_area}`;
    this.store.dispatch(new GetTransportPriceList(params));
  }

  filterTransportList(type, transportResults, load) {
    let filteredArray = [];
    transportResults.forEach((price) => {
      if (price.priceType.name == "Both" || price.priceType.name == "Rentals") {
        if (
          price.transportCharge.name == "Transport" ||
          price.transportCharge.name == "Delivery and Collection"
        ) {
          filteredArray.push(price);
        }
      }
    });
    if (type === "load1") {
      this.selectedTransportLoadOne.results = filteredArray[0];

      const transport = {
        id: this.selectedTransportLoadOne.results.id,
        transport: this.selectedTransportLoadOne.results,
        quantity: load.quantity,
      };

      this.quoteLineItem.transportOne = transport;
      this.transportOneLoading = false;
      this.transportOneHasPricing = true;
      this.showOptionDescription = this.selectedOption != null ? true : false;
    } else if (type === "load2") {
      this.selectedTransportLoadTwo.results = filteredArray[0];

      const transport = {
        id: this.selectedTransportLoadTwo.results.id,
        transport: this.selectedTransportLoadTwo.results,
        quantity: load.quantity,
      };

      this.quoteLineItem.transportTwo = transport;
      this.transportTwoLoading = false;
      this.transportTwoHasPricing = true;
      this.showOptionDescription = this.selectedOption != null ? true : false;
    }
  }

  setSelectedTransportOption() {
    this.selectedTransportOption = this.selectedTransportOption
      ? this.selectedTransportOption
      : this.rentalForm.value.transport_option;

    this.getTransportPriceOptions();
  }

  getTransportPriceOptions() {
    if (
      this.selectedTransportLoad &&
      this.selectedTransportOption &&
      this.selectedSize
    ) {
      this.store.dispatch(new ClearTransportPriceList());
      const params = `?load_size=${this.selectedTransportLoad}&transport_location=${this.createdQuote.shipping_area}`;
      this.store.dispatch(new GetTransportPriceList(params));
    }
  }

  searchCustomers() {
    if (this.searchText !== "") {
      this.store.dispatch(new ClearCustomerList());

      this.store.dispatch(
        new GetCustomerList({
          search: this.searchText,
        })
      );
    }
  }

  setSelectedCustomer(customer) {
    this.customerId = customer.id;
    this.customerSelected = true;
    this.quoteForm
      .get("customer")
      .setValue(`${customer.firstName} ${customer.lastName}`);
  }

  getLocation() {
    let location;

    if (this.isStaff && !this.isCustomer) {
      // if (this.selectedLocation) {
      //   location = this.selectedLocation;
      // } else {
      //   location = this.user.salesRep.locations[0].id;
      // }

      location = this.selectedLocation ? this.selectedLocation : this.user.salesRep.locations[0].id;

    } else if (this.isCustomer) {
      location = this.selectedLocation;
    }

    return location;
  }

  saveQuote() {
    this.lineItemsToAdd = this.quoteCart.length;
    let quoteLocation = this.getLocation();

    const payload: any = {
      quote_status: "new",
      quote_type: "rental",
      quote_validity: this.quoteValidities ? this.quoteValidities[0].id : null,
      building: this.selectedBuildingType,
      organization: this.selectedOrganizationalSector,
      your_reference: "Rev00",
      // customer_account_number: this.quoteForm.value.customer_account_number
      //   ? this.quoteForm.value.customer_account_number
      //   : "",
      // site_allocation: this.quoteForm.value.site_allocation
      //   ? this.quoteForm.value.site_allocation
      //   : "",
      notes: this.quoteForm.value.notes ? this.quoteForm.value.notes : "",
      line_items: [],
      purchase_line_items: [],
      quote_location: quoteLocation,
      shipping: true,
      shipping_address1: this.addressForm.value.shipping_address1,
      shipping_address2: this.addressForm.value.shipping_address2,
      shipping_postal: this.addressForm.value.shipping_postal,
      shipping_province: this.selectedProvince,
      shipping_area: this.selectedArea ? this.selectedArea : null,
      shipping_suburb: this.addressForm.value.shipping_suburb,
    };

    if (this.isStaff) {
      if (this.user.salesRep && !this.isAdmin) {
        payload.quote_owner = this.user.salesRep.id;
      }
      payload.customer = this.customerId;
    }

    if (this.isCustomer) {
      payload.customer = this.user.customer.id;
    }

    this.store
      .select(createRentalQuoteSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.activeStep = 2;
          this.stepper[0].status = "complete";
          this.stepper[1].status = "progress";

          this.getRentalPeriods();
          this.getRentalSizes();
          this.getRentalCategories();
        }
      });

    this.store
      .select(getCreatedRentalQuote)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.createdQuote = quote;
          this.quoteNumber = this.createdQuote.quote_number;

          if (this.isSaProvince && !this.isClassroom) {
            this.getTransportPricing();
          }
        }
      });

    this.store.dispatch(new CreateRentalQuote(payload));
  }

  handleClassroom() {
    const id = this.rentalSizes.findIndex((obj) => obj.size == "7m x 7m");
    const _size = this.rentalSizes[id].id;
    this.selectedSize = _size ? _size : null;

    const _id = this.rentalCategories.findIndex(
      (obj) => obj.name == "Classroom"
    );
    this.selectedRentalCategory = this.rentalCategories[_id].id;

    this.getRentalOptions();
  }

  setAdditionalUnitProperties() {
    const addedUnitPeriod = this.quoteCart[0].selected_period_id;
    const addedUnitMonths = this.quoteCart[0].rental_period;

    this.selectedPeriod = addedUnitPeriod;
    this.rentalForm.get("rental_months").setValue(addedUnitMonths);

    this.periodIsSet = true;
    this.showPeriodDD = false;
    this.rentalMonthsError = false;

    this.getRentalOptions();
  }

  addExtras(checked) {

    if (this.quoteLineItem.extras.length > 0) {
      this.removeExtrasModal.open();
    } else {
      this.showOptionalExtras = checked ? true : false;
      this.showQty = this.showOptionalExtras ? false : true;

      // if (this.showOptionalExtras) {
      //   this.showQty = false;
      // } else {
      //   this.showQty = true;
      // }
    }
  }

  removeExtras() {
    if (this.quoteLineItem.extras.length > 0) {
      let extras = this.quoteLineItem.extras;
      let _quoteLineIdx = null;

      extras.forEach((extra) => {
        let name = extra.name;

        for (let i = 0; i < this.quoteLineItemProducts.length; i++) {
          let extraName = this.quoteLineItemProducts[i].name;
          if (extraName == name) {
            _quoteLineIdx = i;
            break;
          }
        }
        this.quoteLineItemProducts.splice(_quoteLineIdx, 1);
      });

      this.quoteLineItem.extras = [];
      this.showOptionalExtras = false;

      this.removeExtrasModal.close();
    }
  }

  deleteExtra(extra, idx) {
    let _quoteLineIdx;

    for (let i = 0; i < this.quoteLineItemProducts.length; i++) {
      let extraName = this.quoteLineItemProducts[i].name;
      if (extraName == extra.name) {
        _quoteLineIdx = i;
        break;
      }
    }

    this.quoteLineItem.extras.splice(idx, 1);
    this.quoteLineItemProducts.splice(_quoteLineIdx, 1);
  }

  editExtra(extra, idx) {
    this.extraToEdit = idx;
    this.extraToEditName = extra.name;

    this.editExtrasForm.get("product").setValue(extra.name);
    this.editExtrasForm.get("quantity").setValue(extra.quantity);

    this.editOptionalExtraModal.open();
  }

  editOptionalExtraToLineItem() {
    if (!this.limitationError) {
      let _quoteLineIdx;

      for (let i = 0; i < this.quoteLineItemProducts.length; i++) {
        let extraName = this.quoteLineItemProducts[i].name;
        if (extraName == this.extraToEditName) {
          _quoteLineIdx = i;
          break;
        }
      }

      const qty =
        this.editExtrasForm.value.quantity != "" &&
          this.editExtrasForm.value.quantity != null &&
          this.editExtrasForm.value.quantity > 0
          ? this.editExtrasForm.value.quantity * this.unitQty
          : 1 * this.unitQty;

      this.quoteLineItemProducts[_quoteLineIdx].product =
        this.selectedProduct.name;
      this.quoteLineItemProducts[_quoteLineIdx].quantity = qty;

      this.quoteLineItem.extras[this.extraToEdit].product =
        this.selectedProduct.name;
      this.quoteLineItem.extras[this.extraToEdit].quantity = qty;

      this.editExtrasForm.reset();

      this.editOptionalExtraModal.close();

      this.selectedProductId = null;
      this.selectedProduct = null;
      this.extraToEdit = null;
      this.extraToEditName = null;
      this.limitation = "";
      this.limitationError = false;
    }
  }

  addOptionalExtra() {
    this.optionalExtraModal.open();
    this.getProducts();
  }

  showDrawing() {
    const drawingNumber = this.selectedOption.drawingNumber;
    this.drawingImg =
      "../../../../../../assets/rental-images/" + drawingNumber + ".JPG";

    this.drawingModal.open();
  }

  addOptionalExtraToLineItem() {
    if (!this.limitationError) {
      let name =
        this.quoteNumber +
        "," +
        this.quoteLineItem.name +
        "," +
        (this.quoteLineItem.extras.length + 1);
      let qty =
        this.extrasForm.value.quantity != "" &&
          this.extrasForm.value.quantity != null &&
          this.extrasForm.value.quantity > 0
          ? this.extrasForm.value.quantity * this.unitQty
          : 1 * this.unitQty;

      let extra = {
        name,
        idx: this.selectedProductId,
        product: this.selectedProduct.name,
        quantity: qty,
      };

      this.quoteLineItem.extras.push(extra);
      this.quoteLineItemProducts.push(extra);

      this.extrasForm.reset();

      this.optionalExtraModal.close();
      this.selectedProductId = null;
      this.selectedProduct = null;
      this.limitation = "";
      this.limitationError = false;
    }
  }

  setProduct() {
    this.selectedProductId = this.extrasForm.value.product;

    let _id = this.products.findIndex(
      (obj) => obj.id == this.selectedProductId
    );
    this.selectedProduct = this.products[_id];
  }

  addToCart() {
    if (this.isClassroom) {
      this.quoteLineItem.quantity = this.unitQty;
    }

    this.quoteCart.push(this.quoteLineItem);
    this.canSave = this.quoteCart.length > 0 ? true : false;

    this.rentalForm.reset();
    this.quoteForm.reset();
    this.extrasForm.reset();

    this.showOptionalExtras = false;
    this.showOptionDescription = false;
    this.showRentalOptions = false;
    this.showQty = true;

    this.quoteLineItem = {};

    this.selectedPeriod = null;
    this.selectedProduct = null;
    this.selectedSize = null;
    this.selectedOption = null;

    this.rentalSizes = [];
    this.showTransportSize = false;
    this.showTransport = false;
    this.showSize = false;
    this.isClassroom = false;
    this.showTransportGuide = false;

    this.unitAddedModal.open();

    this.setAdditionalUnitProperties();
  }

  completeQuote() {
    this.saveLineItems();
  }

  saveProducts() {
    let addon = this.quoteLineItemProducts[0];
    this.saveProduct(addon);

    this.store
      .select(getCreatedProductConfiguration)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((products) => {
        if (products) {
          let productsAdded = products.length;
          this.addOnsToAdd = this.quoteLineItemProducts.length;

          if (this.addOnsToAdd == productsAdded) {
            const route = `${AppRoutes.PWA + "quotes/edit/rental/" + this.createdQuote.id
              }`;
            this.router.navigate([route]);
          } else {
            if (productsAdded > 0) {
              let itemIdx = this.quoteLineItemProducts[productsAdded];
              this.saveProduct(itemIdx);
            }
          }
        }
      });
  }

  saveProduct(addon) {
    let line_item = this.getLineItemId(addon.name);

    let payload = {
      name: addon.name,
      product: addon.idx,
      quantity: addon.quantity,
      line_item,
    };

    this.store.dispatch(new CreateProductConfiguration(payload));
  }

  getLineItemId(productName) {
    let lineItemId = "";

    for (let i = 0; i < this.addedLineItems.length; i++) {
      let lineItemName = this.addedLineItems[i].name;
      let unitName = productName.split(",")[1];

      if (lineItemName == unitName) {
        lineItemId = this.addedLineItems[i].id;
        break;
      }
    }
    return lineItemId;
  }

  saveLineItems() {
    let item = this.quoteCart[0];
    this.saveLineItem(item);

    this.store
      .select(getCreatedLineItems)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items) => {
        if (items) {
          this.addedLineItems = items;

          if (this.addedLineItems.length == this.quoteCart.length) {
            if (this.quoteLineItemProducts.length == 0) {
              const route = `${AppRoutes.PWA + "quotes/edit/rental/" + this.createdQuote.id
                }`;
              this.router.navigate([route]);
            } else {
              this.saveProducts();
            }
          } else {
            if (this.addedLineItems.length > 0) {
              let itemIdx = this.quoteCart[this.addedLineItems.length];
              this.saveLineItem(itemIdx);
            }
          }
        }
      });
  }

  saveLineItem(item) {
    let payload = {
      name: item.name,
      rental_option: item.item.id,
      rental_period: item.rental_period,
      product_configurations: [],
      delivery_charge: item.transportOne ? item.transportOne.id : null,
      delivery_charge_qty: item.transportOne ? item.transportOne.quantity : null,
      delivery_charge_extra: item.transportTwo ? item.transportTwo.id : null,
      delivery_charge_extra_qty: item.transportTwo ? item.transportTwo.quantity : null,
      delivery_charge_optional: item.transportJoin
        ? item.transportJoin.id
        : null,
      quantity: item.quantity,
      quote: this.createdQuote.id,
    };

    this.store.dispatch(new CreateRentalLineItem(payload));
  }

  calculateLoadSize() {
    const unitQty = this.rentalForm.value.unit_qty
      ? this.rentalForm.value.unit_qty
      : 1;
    let match;

    // Selected Rental Option Size
    const _id = this.rentalSizes.findIndex(
      (obj) => obj.id == this.selectedSize
    );
    const size = this.rentalSizes[_id].size;

    let min = size.split("x")[0].replace(/\s/g, "").charAt(0);
    let max = size.split("x")[1].replace(/\s/g, "").charAt(0);
    min = parseInt(min);
    max = parseInt(max);

    if (unitQty == 2) {
      if (min == 3) {
        match = this.getPerfectMatch(min, 6);
      } else if (min == 6) {
        match = this.getPerfectMatch(min, 12);
      }
    } else {
      match = this.getPerfectMatch(min, max);
    }

    if (match != null) {
      this.rentalLoading = false;

      this.selectedTransportLoad = match;
      this.setSelectedTransportLoad();
    } else {
      // no exact match found, look for closest match
      match = this.findClosestMatch(min, max);
      if (match != null) {
        this.rentalLoading = false;

        this.selectedTransportLoad = match;
        this.setSelectedTransportLoad();
      } else {
        this.rentalLoading = false;

        // no matches found, select load size manually
        this.showTransport = true;
        this.showTransportSize = true;
      }
    }
  }

  getPerfectMatch(unitWidth, unitLength) {
    let _id = null;

    for (let i = 0; i < this.transportLoads.length; i++) {
      let loadSize = this.transportLoads[i].size;

      let length = loadSize.split("m x")[0].replace(/\s/g, "");
      let width = loadSize.split("x")[1].replace(/\s/g, "").charAt(0);
      length = parseInt(length);
      width = parseInt(width);

      if (unitWidth == width && unitLength == length) {
        _id = this.transportLoads[i].id;
        break;
      }
    }

    return _id;
  }

  findClosestMatch(unitWidth, unitLength) {
    let _id = null;
    let matchedItems = [];

    for (let i = 0; i < this.transportLoads.length; i++) {
      let loadSize = this.transportLoads[i].size;

      let length = loadSize.split("m x")[0].replace(/\s/g, "");
      let width = loadSize.split("x")[1].replace(/\s/g, "").charAt(0);
      length = parseInt(length);
      width = parseInt(width);

      if (unitLength <= length && unitWidth <= width) {
        matchedItems.push(this.transportLoads[i]);
      }
    }

    if (matchedItems.length > 0) {
      matchedItems.sort((a, b) => (a.size > b.size ? 1 : -1));
      _id = matchedItems[0].id;
    }

    return _id;
  }

  validateMinMaxMonths() {
    const _id = this.rentalPeriods.findIndex(
      (obj) => obj.id == this.selectedPeriod
    );

    const period = this.rentalPeriods[_id].period;
    const actual = this.rentalForm.value.rental_months;

    let isMaxMonths = period.search("24");

    if (isMaxMonths == 0) {
      if (actual < 24) {
        this.rentalMonthsError = true;
        this.rentalMonthsErrorMsg = `Actual months must be at least 24`;
      } else {
        this.rentalMonthsError = false;
        this.rentalMonthsErrorMsg = "";
      }
    } else {
      let min = period.split("-")[0].replace(/\s/g, "");
      let max = period.split("-")[1].replace(/\s/g, "");
      min = parseInt(min);
      max = parseInt(max);

      if (actual == "" || actual < min || actual > max) {
        this.rentalMonthsError = true;
        this.rentalMonthsErrorMsg = `Actual months must be between ${min} and ${max}`;
      } else {
        this.rentalMonthsError = false;
        this.rentalMonthsErrorMsg = "";
      }
    }

    this.quoteLineItem.rental_period = this.rentalForm.value.rental_months;
  }

  validateLimitation(value) {
    if (this.selectedProduct && this.selectedProductId) {
      const productLimitation = parseInt(this.selectedProduct.limitation);

      if (productLimitation > 0) {
        let limitError = value > productLimitation ? true : false;

        if (limitError) {
          this.limitationError = true;
          this.limitation = productLimitation;
        } else {
          this.limitationError = false;
          this.limitation = "";
        }
      } else {
        this.limitationError = false;
        this.limitation = "";
      }
    }
  }

  setUnitQty() {
    this.unitQty =
      this.rentalForm.value.unit_qty != null && this.rentalForm.value.unit_qty > 1
        ? this.rentalForm.value.unit_qty
        : 1;
    this.qtyEntered = this.unitQty > 0 ? true : false;
  }

  subscribeToSubjects() {
    this.loadOneQtyUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.setSelectedTransportLoadOne()
      });

    this.loadTwoQtyUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.setSelectedTransportLoadTwo()
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelationSubscription.unsubscribe();
  }
}
